<template>
  <div>
    <px-view-header title="Alçadas de compra" title-class="font-weight-bold"
                    :breadcrumbs="breadcrumbs"/>
    <div v-if="loading">
      <px-circular-loading/>
    </div>
    <div class="mt-5" v-if="!loading && criteriosEnum && criteriosEnum.length > 0">
      <px-alcadas-list :alcadas="alcada"
                       :opcoes-criterios="opcoesCriterios"
                       :criterios-enum="criteriosEnum"
                       :loading-save="loadingSave"
                       :loading-remove="loadingRemove"
                       :roles-aprovador="['ROLE_APROVADOR_COMPRAS']"
                       :readonly="!hasRoleConfiguradorAlcadaCompra"
                       edicao-sem-aprovacao
                       @save="onClickSave"
                       @remove="onClickRemove"/>
    </div>
    <px-confirm-dialog ref="confirm"/>
  </div>
</template>

<script>
import { isArray } from 'lodash';
import { mapGetters } from 'vuex';
import AlcadasList from '@/modules/alcadas/components/AlcadasList.vue';
import { CriteriosAprovacaoCompra } from 'px-business-components';
import AlcadasCompraService from '@/modules/alcadas/alcadas-compra-service';
import ErpCampoService from '@/api/erp-campo-service';
import alcadasCadastrosAdicionaisConstants
  from '@/modules/alcadas/alcadas-cadastros-adicionais-constants';
import { mapCadastroAdicionalAlcadaByErp } from './components/alcadas-erp-utils';

const criteriosValor = [CriteriosAprovacaoCompra.keys.VALOR_TOTAL.key,
  CriteriosAprovacaoCompra.keys.FAIXA_VALOR.key];


export default {
  data() {
    return {
      loading: false,
      loadingSave: false,
      loadingRemove: false,
      alcada: null,
      criteriosEnum: [],
      opcoesCriterios: [],
      breadcrumbs: [
        { text: 'Configurações' },
        { text: 'Alçadas de compra' },
      ],
    };
  },
  computed: {
    ...mapGetters('user', ['hasRoleConfiguradorAlcadaCompra']),
    ...mapGetters('entidade', ['erpCamposIntegracao']),
  },
  methods: {
    getValueFromCriterio(criterio) {
      if (criteriosValor.includes(criterio.campo)) {
        return criterio.valor || criterio.values[0];
      }
      return criterio.values;
    },
    buildAlcadaOnLoad(alcada) {
      return {
        ...alcada,
        regras: alcada.regras.map(regra => ({
          ...regra,
          $$id: regra.id,
          criterios: regra.criterios.map((criterio) => {
            if (criterio.campo === CriteriosAprovacaoCompra.keys.CADASTRO_ADICIONAL.key) {
              return {
                campo: criterio.values[0]?.tipo,
                excecao: criterio.excecao,
                values: this.getValueFromCriterio(criterio),
                $$id: criterio.id,
              };
            }
            return {
              ...criterio,
              values: this.getValueFromCriterio(criterio),
              $$id: criterio.id,
            };
          }),
        })),
      };
    },
    getAlcadas() {
      this.loading = true;
      return AlcadasCompraService.get()
        .then(({ data }) => {
          this.alcada = data ? this.buildAlcadaOnLoad(data) : null;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onClickSave(payload) {
      this.loadingSave = true;
      this.saveFunction(payload)
        .then(() => {
          this.$toast.success('Alçadas atualizadas');
          this.getAlcadas();
        })
        .finally(() => {
          this.loadingSave = false;
        });
    },
    mapValues(values) {
      if (isArray(values)) {
        return values.map((value) => {
          if (typeof value === 'object') {
            return { id: value.id };
          }
          return value;
        });
      }
      return values;
    },
    mapValueProperyName(value) {
      if (isArray(value)) {
        return 'values';
      }
      return 'valor';
    },
    buildCriterios(criterio) {
      const opcaoCriterio = this.opcoesCriterios.find(c => c.key === criterio.campo);
      const builtCriterio = {
        $$id: undefined,
        excecao: criterio.excecao || false,
        id: criterio.id ?? undefined,
        campo: opcaoCriterio?.isCampoAdicional
          ? CriteriosAprovacaoCompra.keys.CADASTRO_ADICIONAL.key : criterio.campo,
      };
      builtCriterio[this.mapValueProperyName(criterio.values)] = this.mapValues(criterio.values);
      return builtCriterio;
    },
    buildPayload(payload) {
      const { regras, criaHierarquia } = payload;
      return {
        id: this.alcada ? this.alcada.id : undefined,
        regras: regras.map((regra, index) => ({
          ...regra,
          $$id: undefined,
          sequencia: index + 1,
          criterios: regra.criterios.map(this.buildCriterios),
        })),
        criaHierarquia,
      };
    },
    saveFunction(payload) {
      const builtPayload = this.buildPayload(payload);
      if (this.alcada) {
        return AlcadasCompraService.update(this.alcada.id, builtPayload);
      }
      return AlcadasCompraService.create(builtPayload);
    },
    onClickRemove() {
      this.$refs.confirm.open('Você deseja remover essa configuração?',
        'Esta operação não pode ser desfeita',
        { width: 400 })
        .then((confirm) => {
          if (!confirm) {
            return;
          }
          this.removeAlcadas();
        });
    },
    removeAlcadas() {
      this.loadingRemove = true;
      AlcadasCompraService.remove(this.alcada.id)
        .then(() => {
          this.$toast.success('Alçadas removidas');
          this.getAlcadas();
        })
        .finally(() => {
          this.loadingRemove = false;
        });
    },
    buildOpcoesCriterios(cadastrosAdicionaisDisponiveis, opcoesCadastrosAdicionais) {
      const opcoesCriterios = CriteriosAprovacaoCompra.values.filter(
        v => v.key !== CriteriosAprovacaoCompra.keys.CADASTRO_ADICIONAL.key,
      );
      const opcoesDisponiveis = opcoesCadastrosAdicionais
        .filter(o => cadastrosAdicionaisDisponiveis.includes(o.key))
        .map(opcao => mapCadastroAdicionalAlcadaByErp(this.erpCamposIntegracao, opcao));

      const opcoes = [...opcoesCriterios, ...opcoesDisponiveis];
      this.criteriosEnum = opcoes;
      this.opcoesCriterios = opcoes;
    },
    getCamposErp() {
      if (!this.erpCamposIntegracao) {
        this.buildOpcoesCriterios([], []);
        return Promise.resolve({});
      }
      this.loading = true;
      return ErpCampoService.getCamposGeraisCompraPorErp(this.erpCamposIntegracao)
        .then(({ data }) => {
          this.buildOpcoesCriterios(data, alcadasCadastrosAdicionaisConstants);
        }).finally(() => {
          this.loading = false;
        });
    },
  },
  created() {
    this.getCamposErp()
      .then(() => {
        this.getAlcadas();
      });
  },
  components: {
    pxAlcadasList: AlcadasList,
  },
};
</script>
